import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';

import type { ChipProps } from '@mui/material/Chip';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';

import IconButton from 'src/components/ui/IconButton';

import type { GooglePOIFiltersValues } from 'src/hooks/useGooglePOIFilters';

import { styledComponent } from 'src/utils/styled';
import theme from 'src/utils/theme';

import { colours } from 'src/constants/colours';

const GoogleMapPOIFiltersContainer = styledComponent(Box)<{
    $fullscreenControl: boolean;
}>`
    width: calc(100% - ${({ $fullscreenControl }) => {
        let leftAndRightContentWidth = 2; // left and right initial container marging (to avoid tags to be behind arrow button)
        if ($fullscreenControl) {
            leftAndRightContentWidth += 2 * (10 / 16); // left and right fullscreenControl marging
            leftAndRightContentWidth += 40 / 16; // fullscreenControl width
            leftAndRightContentWidth += 1 * (10 / 16); // right container marging
        }
        return leftAndRightContentWidth;
    }}rem);
    align-items: center;
    position: relative;
    height: 0; /* to don't take space in the HTML, but the children has the height */
    top: 2rem; /* ScrollerContainer height = 4rem (to have bo shadows), Chips have 2rem height so the middle need to be at 2rem*/
    left: 1.625rem; /* 10px margin left like other control + 1rem = half width of the arrow to don't see the chip behind */
    display: flex;
`;

const ScrollerContainer = styledComponent(Box)`
    z-index: 10;
    overflow-x: scroll;
    height: 4rem; /* To have box shadows of children visible */
    display: flex;
    align-items: center;
    /* Hide scrollbar */
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    > div {
        margin: 0 0.375rem;
    }
    > div:first-of-type {
        margin-left: 0rem;
    }
    > div:last-of-type {
        margin-right: 0rem;
    }
`;

const StyledArrowButtonWrapper = styledComponent(Box)<{
    $side: 'right' | 'left';
}>`
    height: 2rem;
    align-items: center;
    position: absolute;
    top: -1rem; /* half the height to be center */
    left: ${({ $side }) => ($side === 'left' ? '-1rem' : 'unset')};
    right: ${({ $side }) => ($side === 'right' ? '-1rem' : 'unset')};
    z-index: 11;
    display: flex;
    align-items: center;
    .MuiButtonBase-root {
        box-shadow: 0 0.25rem 0.5rem 0 ${colours.blackOpacity.O16};
    }
`;

const StyledChip = styledComponent(Chip)<ChipProps>`
    box-shadow: 0 0.25rem 0.5rem 0 ${colours.blackOpacity.O16};
    height: 2rem;

    /* override color="info" for this component */
    ${(props: ChipProps) =>
        props.color === 'info' &&
        `
        color: ${theme.palette.primary.main};
        background-color: ${colours.grey.G50};
        &:hover {
            background-color: ${colours.grey.G100};
        }
    `}
`;

const GoogleMapPOIFilters: React.FC<
    GooglePOIFiltersValues & { fullscreenControl: boolean; mapTypeControl: boolean }
> = ({
    hideAttraction,
    hideBusiness,
    hideGovernment,
    hideMedical,
    hidePark,
    hidePlaceOfWork,
    hideSchool,
    hideSport,
    setHideAttraction,
    setHideBusiness,
    setHideGovernment,
    setHideMedical,
    setHidePark,
    setHidePlaceOfWork,
    setHideSchool,
    setHideSport,
    fullscreenControl,
    mapTypeControl,
}) => {
    const { t } = useTranslation(['ad']);
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const [scrollX, setScrollX] = useState(0);
    const [scrollEnd, setScrollEnd] = useState(false);

    const scrollerRefCurrentIsDefined = Boolean(scrollerRef.current);

    useEffect(() => {
        if (scrollerRef.current) {
            setScrollX(scrollerRef.current.scrollLeft);
            if (
                Math.floor(scrollerRef.current.scrollWidth - scrollerRef.current.scrollLeft) <=
                scrollerRef.current.offsetWidth
            ) {
                setScrollEnd(true);
            } else {
                setScrollEnd(false);
            }
        }
    }, [scrollerRefCurrentIsDefined]);

    const slide = (shift: number) => {
        if (scrollerRef.current) {
            scrollerRef.current.scrollBy({
                left: shift,
                behavior: 'smooth',
            });

            setScrollX(scrollX + shift);
            if (
                Math.floor(scrollerRef.current.scrollWidth - scrollerRef.current.scrollLeft) <=
                scrollerRef.current.offsetWidth
            ) {
                setScrollEnd(true);
            } else {
                setScrollEnd(false);
            }
        }
    };

    const scrollCheck = () => {
        if (scrollerRef.current) {
            setScrollX(scrollerRef.current.scrollLeft);
            if (
                Math.floor(scrollerRef.current.scrollWidth - scrollerRef.current.scrollLeft) <=
                scrollerRef.current.offsetWidth
            ) {
                setScrollEnd(true);
            } else {
                setScrollEnd(false);
            }
        }
    };

    const chips: Array<{
        label: ChipProps['label'];
        action: () => void;
        name: string;
        isActive: boolean;
    }> = useMemo(
        () => [
            {
                label: t('ad:mapPOIPark'),
                action: () => setHidePark((previousValue) => !previousValue),
                name: 'HidePark',
                isActive: hidePark,
            },
            {
                label: t('ad:mapPOIBusiness'),
                action: () => setHideBusiness((previousValue) => !previousValue),
                name: 'HideBusiness',
                isActive: hideBusiness,
            },
            {
                label: t('ad:mapPOIAttractions'),
                action: () => setHideAttraction((previousValue) => !previousValue),
                name: 'HideAttraction',
                isActive: hideAttraction,
            },
            {
                label: t('ad:mapPOIGovernments'),
                action: () => setHideGovernment((previousValue) => !previousValue),
                name: 'HideGovernment',
                isActive: hideGovernment,
            },
            {
                label: t('ad:mapPOIHealth'),
                action: () => setHideMedical((previousValue) => !previousValue),
                name: 'HideMedical',
                isActive: hideMedical,
            },
            {
                label: t('ad:mapPOIPlacesOfWorship'),
                action: () => setHidePlaceOfWork((previousValue) => !previousValue),
                name: 'HidePlaceOfWork',
                isActive: hidePlaceOfWork,
            },
            {
                label: t('ad:mapPOISchool'),
                action: () => setHideSchool((previousValue) => !previousValue),
                name: 'HideSchool',
                isActive: hideSchool,
            },
            {
                label: t('ad:mapPOISport'),
                action: () => setHideSport((previousValue) => !previousValue),
                name: 'HideSport',
                isActive: hideSport,
            },
        ],
        [
            hideAttraction,
            hideBusiness,
            hideGovernment,
            hideMedical,
            hidePark,
            hidePlaceOfWork,
            hideSchool,
            hideSport,
            setHideAttraction,
            setHideBusiness,
            setHideGovernment,
            setHideMedical,
            setHidePark,
            setHidePlaceOfWork,
            setHideSchool,
            setHideSport,
            t,
        ],
    );

    return (
        <GoogleMapPOIFiltersContainer $fullscreenControl={fullscreenControl}>
            {scrollX >= 1 && (
                <StyledArrowButtonWrapper $side="left">
                    <IconButton
                        onClick={() => slide(-200)}
                        color="white"
                        iconProps={{
                            name: 'ChevronLeft',
                            size: '1rem',
                        }}
                    />
                </StyledArrowButtonWrapper>
            )}
            <ScrollerContainer ref={scrollerRef} onScroll={scrollCheck}>
                {chips.map(({ label, action, name, isActive }) => (
                    <StyledChip key={name} color={isActive ? 'info' : 'primary'} label={label} onClick={action} />
                ))}
            </ScrollerContainer>
            {!scrollEnd && (
                <StyledArrowButtonWrapper $side="right">
                    <IconButton
                        onClick={() => slide(+200)}
                        color="white"
                        iconProps={{
                            name: 'ChevronRight',
                            size: '1rem',
                        }}
                    />
                </StyledArrowButtonWrapper>
            )}
        </GoogleMapPOIFiltersContainer>
    );
};

export default GoogleMapPOIFilters;
